import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { ESounds, SoundsService } from "./sound.service";
import { LoadingService } from "./loading.service";
import { wrangleObMessageInPlace, wrangleFuturesObMessageInPlace } from "src/app/@core/utils/ob-normalize";


@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  public ws: WebSocket | null = null;
  private wsConnected = false;
  private clientId = '';
  private eventSubject = new Subject<{ event: string; data: any }>();
  
  // === NEW: Track if we're destroyed to prevent zombie connections ===
  private destroyed = false;

  constructor(
    private soundsService: SoundsService,
    private loadingService: LoadingService
  ) {}

  ngOnDestroy() {
    this.destroyed = true;
    this.obSocketDisconnect();
    this.eventSubject.complete();
  }

  /**
   * Connects to the backend WebSocket, if used.
   * For in-app events only, call emitEvent instead.
   */
  public obSocketConnect(url: string): void {
    if (this.destroyed) {
      console.warn('SocketService destroyed, refusing to connect');
      return;
    }
    
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket is already connected or connecting.');
      return;
    }
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      if (this.destroyed) {
        this.ws?.close();
        return;
      }
      this.wsConnected = true;
      console.log('OB WebSocket connected');
    };

    this.ws.onclose = (event) => {
      this.wsConnected = false;
      console.error('OB WebSocket disconnected:', event.reason);
    };

    this.ws.onerror = (err) => {
      this.wsConnected = false;
      console.error('OB WebSocket connection error:', err);
    };

    this.ws.onmessage = (msg) => {
      if (this.destroyed) return;
      
      try {
        const data = JSON.parse(msg.data);
        const eventName = data.event || data.type || 'unknown';
        console.log('incoming websocket '+JSON.stringify(data))
        if (eventName === 'connected' && data.id) {
          this.clientId = data.id;
          console.log('[OB WS] Assigned client id:', data.id);
        }

        // Emit event for in-app subscribers
        this.emitEvent(eventName, data);

        if (eventName === 'new-channel') {
          console.log("[Caller] About to call onInit, this.events$ =", this.events$);
        }

        if(eventName === 'orderbook-data'){
          const ob = wrangleFuturesObMessageInPlace(data)
          console.log('normalized '+JSON.stringify(ob))
        }

      } catch (e) {
        console.error('WebSocket message parse error:', e, msg.data);
      }
    };
  }

  public obSocketDisconnect(): void {
    if (this.ws) {
      this.ws.close(1000, 'client-close');
      this.ws = null;
      this.wsConnected = false;
    }
  }

  /** Emits an event to in-app subscribers (no networking) */
  public emitEvent(event: string, data: any): void {
    if (this.destroyed) return;
    console.log('event emitter '+event +' '+JSON.stringify(data))
    this.eventSubject.next({ event, data });
  }

  /** Observable for event subscription */
  get events$() {
    return this.eventSubject.asObservable();
  }

  /** Send a message over the backend WebSocket, if still needed */
  public send(event: string, data: any): void {
    if (!this.ws || !this.wsConnected || this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected; cannot send message');
      return;
    }

    // Events that expect flattened payloads
    const flatEvents = ['new-order', 'close-order','::swap','update-orderbook'];

    const isFlat = flatEvents.some(e => event.endsWith(e))
    const msg = isFlat 
        ? JSON.stringify({ event, ...data })      // flattened
        : JSON.stringify({ event, data });        // nested

    console.log('[SocketService.send] sending:', msg);
    this.ws.send(msg);
  }

  get obSocketConnected(): boolean {
    return this.wsConnected;
  }
}
